import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { GitCompare, Check, X, Bot } from "lucide-react";
import { motion } from "framer-motion";

export function SecondOpinionPanel({ secondOpinion, recommendation, onRun, loading }) {
  const { t } = useTranslation();
  return (
    <div className="card-crisp overflow-hidden" data-testid="second-opinion-panel">
      <div className="flex items-center justify-between border-b border-border bg-secondary/40 p-5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-ikb/10">
            <GitCompare className="h-4 w-4 text-ikb" />
          </div>
          <div>
            <h3 className="font-display text-base font-bold">{t("secondOpinion.title")}</h3>
            <p className="text-xs text-muted-foreground">
              {t("secondOpinion.subtitle")}
            </p>
          </div>
        </div>
        {!secondOpinion && (
          <Button
            onClick={onRun}
            disabled={loading}
            variant="outline"
            data-testid="run-second-opinion-btn"
          >
            {loading ? t("secondOpinion.running") : t("secondOpinion.run")}
          </Button>
        )}
      </div>

      {secondOpinion && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-6"
          data-testid="second-opinion-result"
        >
          <div
            className={`mb-5 flex items-center gap-2 rounded-md p-3 text-sm font-medium ${
              secondOpinion.agrees_with_primary
                ? "bg-success/10 text-success"
                : "bg-warning/10 text-warning"
            }`}
            data-testid="second-opinion-verdict"
          >
            {secondOpinion.agrees_with_primary ? (
              <Check className="h-4 w-4" />
            ) : (
              <X className="h-4 w-4" />
            )}
            {secondOpinion.agrees_with_primary
              ? t("secondOpinion.agrees")
              : t("secondOpinion.diverges")}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-md border border-border bg-card p-4">
              <p className="mb-1 text-xs font-semibold uppercase tracking-overline text-ikb">
                {t("secondOpinion.primaryPick")}
              </p>
              <p className="text-sm font-semibold">{secondOpinion.primary_recommendation}</p>
            </div>
            <div className="rounded-md border border-border bg-card p-4">
              <p className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-overline text-muted-foreground">
                <Bot className="h-3.5 w-3.5" /> {t("secondOpinion.secondPick")}
              </p>
              <p className="text-sm font-semibold" data-testid="second-opinion-pick">
                {secondOpinion.recommended_option_name}
              </p>
            </div>
          </div>

          <div className="mt-5">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-overline text-muted-foreground">
              {t("secondOpinion.reasoning")}
            </p>
            <p className="text-sm leading-relaxed">{secondOpinion.reasoning_summary}</p>
          </div>

          {secondOpinion.key_factors?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {secondOpinion.key_factors.map((k, i) => (
                <span
                  key={`${k}-${i}`}
                  className="rounded-full border border-border bg-secondary px-2.5 py-1 text-xs"
                >
                  {k}
                </span>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
